import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { buildPreviewUrl, fetchPreviewToken } from '@/utils/preview';

const REFRESH_INTERVAL_MS = 12 * 60 * 1000;

interface AuthenticatedWebsitePreviewProps {
  section?: string | null;
  patch?: Record<string, unknown>;
  viewport?: PreviewViewport;
}

export type PreviewViewport = 'desktop' | 'tablet' | 'mobile';

const VIEWPORT_WIDTHS: Record<PreviewViewport, number> = {
  desktop: 1440,
  tablet: 768,
  mobile: 390,
};

const AuthenticatedWebsitePreview: React.FC<AuthenticatedWebsitePreviewProps> = ({ section, patch, viewport }) => {
  const [previewUrl, setPreviewUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !viewport || !previewUrl) return;
    const updateSize = () => {
      const bounds = container.getBoundingClientRect();
      setContainerSize({ width: bounds.width, height: bounds.height });
    };
    const observer = new ResizeObserver(([entry]) => {
      setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    updateSize();
    observer.observe(container);
    return () => observer.disconnect();
  }, [viewport, previewUrl]);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const { token, slug } = await fetchPreviewToken();
        if (!cancelled && token && slug) {
          setPreviewUrl(buildPreviewUrl(slug, token));
          setFailed(false);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    };

    void refresh();
    const intervalId = window.setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  const sendPreview = () => {
    if (!section) return;
    iframeRef.current?.contentWindow?.postMessage({
      type: 'onboarding-section-preview',
      section,
      patch,
    }, '*');
  };

  useEffect(() => {
    sendPreview();
  }, [section, patch, previewUrl]);

  useEffect(() => {
    const handleReady = (event: MessageEvent) => {
      if (event.source === iframeRef.current?.contentWindow && event.data?.type === 'onboarding-preview-ready') {
        sendPreview();
      }
    };
    window.addEventListener('message', handleReady);
    return () => window.removeEventListener('message', handleReady);
  }, [section, patch]);

  if (!previewUrl && !failed) {
    return <div className="flex min-h-100 items-center justify-center bg-muted/40"><Spinner size="lg" /></div>;
  }
  if (failed && !previewUrl) {
    return <div className="flex min-h-100 items-center justify-center bg-muted/40 px-6 text-center text-sm text-muted-foreground">Preview is temporarily unavailable. Sign in again or retry shortly.</div>;
  }

  const viewportWidth = viewport ? VIEWPORT_WIDTHS[viewport] : 0;
  const scale = viewport && containerSize.width
    ? Math.min(1, containerSize.width / viewportWidth)
    : 1;
  const viewportHeight = viewport
    ? (containerSize.height > 0 ? containerSize.height / scale : 1)
    : undefined;

  return (
    <div ref={containerRef} className="relative h-full min-h-100 w-full overflow-hidden bg-muted/40">
      <iframe
        ref={iframeRef}
        key={previewUrl}
        src={previewUrl}
        title="Authenticated website preview"
        className={viewport ? 'absolute left-1/2 top-0 border-0 bg-white shadow-lg' : 'h-full min-h-100 w-full bg-white'}
        style={viewport ? {
          width: `${viewportWidth}px`,
          height: `${viewportHeight}px`,
          transform: `translateX(-50%) scale(${scale})`,
          transformOrigin: 'top center',
        } : undefined}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        onLoad={sendPreview}
      />
    </div>
  );
};

export default AuthenticatedWebsitePreview;